import {
  ApplicationConfig,
  isDevMode,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import { provideAuth } from '@lyceo/shared/auth/feature-main';
import { provideSentry } from '@techniek-team/sentry-web';
import { ActionReducer, provideState, provideStore } from '@ngrx/store';
import {
  provideRouterStore,
  routerReducer,
  RouterState,
} from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';
import {
  initialUserState,
  USER_FEATURE_KEY,
  UserEffects,
  userReducer,
} from '@lyceo/homi-dashboard/user/data-access';
import { provideApplicationStore } from '@lyceo/shared/data-access';
import { PlanMode, providePlan } from '@lyceo/shared/plan/util-provider';
import { provideHomiSettings } from '@lyceo/shared/homi-settings/data-access';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ENVIRONMENT } from '@lyceo/homi-dashboard/shared/util-environment';
import { localStorageSync } from 'ngrx-store-localstorage';
import {
  getLocalStorageConfig,
  WALKTHROUGH_MODAL,
} from '@lyceo/shared/walkthrough/util';
import { WalkthroughModalComponent } from '@lyceo/shared/walkthrough/feature-main';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideFeedback } from '@lyceo/shared/feedback/util';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoaderService } from '@lyceo/shared/translate/feature-main';

registerLocaleData(localeNl, 'nl');

const HOMI_STORAGE: string = '__lyceo-homi-dashboard';

export function localStorageSyncReducer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reducer: ActionReducer<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ActionReducer<any> {
  return localStorageSync(getLocalStorageConfig(HOMI_STORAGE))(reducer);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: WALKTHROUGH_MODAL, useValue: WalkthroughModalComponent },
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptorsFromDi()),
    provideStore({
      router: routerReducer,
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      features: {
        pause: false,
        lock: true,
      },
      connectInZone: true,
    }),
    provideRouterStore({
      routerState: RouterState.Minimal,
    }),
    provideState(USER_FEATURE_KEY, userReducer, {
      initialState: initialUserState,
      metaReducers: [localStorageSyncReducer],
    }),
    provideEffects(UserEffects),
    provideApplicationStore(),
    providePlan({
      homiApiUrl: environment.homiApiUrl,
      mode: PlanMode.INSTRUCTOR,
      feedback: true,
    }),
    provideFeedback({
      release: environment.release,
      name: 'Homi dashboard',
      email: 'supportoefenen@lyceo.nl',
    }),
    provideHomiSettings({
      homiApiUrl: environment.homiApiUrl,
    }),
    provideAuth({
      oAuthConfig: environment.authConfig,
      authModuleConfig: environment.authModuleConfig,
      storagePrefix: HOMI_STORAGE,
      initialize: true,
      setSentryUser: true,
    }),
    provideIonicAngular({
      rippleEffect: false,
      useSetInputAPI: true,
    }),
    provideSentry(
      {
        environment: environment.environment,
        dsn: environment.sentryDsn,
        release: environment.release,
        autoSessionTracking: true,
        enableTracing: true,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        profilesSampleRate: 0,
      },
      //withTracing(),
    ),
    provideTransloco({
      config: {
        availableLangs: ['nl'],
        defaultLang: 'nl',
        reRenderOnLangChange: false,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoaderService,
    }),
    provideZoneChangeDetection(),
  ],
};
